import { __awaiter } from '../../node_modules/tslib/tslib.es6.js';
import { Actions, Action } from '../../ai-elements/actions.js';
import { Shimmer } from '../../ai-elements/shimmer.js';
import brandclub_colors from '../../colors/brandclubColors.js';
import { useTheme } from '@mui/material';
import { CopyIcon, ThumbsUpIcon, ThumbsDownIcon, ShareIcon } from 'lucide-react';
import React__default, { useState } from 'react';
import { toast } from 'sonner';
import { getHeadersWithUniqueVisitorId } from '../../Auth/index.js';
import { setConversationSharing, sendMessageFeedback } from '../api.js';
import { renderMessageContentWithCarousels } from '../utils/messageRenderer.js';
import { ChainOfThought } from './ChainOfThought.js';
import { MessageBubbleWidget } from '@brandclub/bc-widgets';

const ChatMessageComponent = React__default.forwardRef(({ role, content, bottomRef, isTyping = false, isStreaming = false, isLastMessage = false, messageIndex, conversationId, feedback, productMetadataMap, streamingProductGroups, currentStatus, previewSearchQueries = [], previewQueryProducts = [], featuredProduct, streamingFeaturedProduct, imageUrl, imageData, imageMimeType, onUpdateFeedback, apiBaseUrl, }) => {
    const theme = useTheme();
    const isUser = role === 'user';
    // Initialize feedback state from props
    const [liked, setLiked] = useState((feedback === null || feedback === void 0 ? void 0 : feedback.type) === 'like');
    const [disliked, setDisliked] = useState((feedback === null || feedback === void 0 ? void 0 : feedback.type) === 'dislike');
    const formatProductForCopy = (product, isFeatured) => {
        const parts = [`Title: ${product.title}`];
        if (product.brandName) {
            parts.push(`Brand: ${product.brandName}`);
        }
        if (product.retailPrice !== undefined) {
            parts.push(`Price: $${product.retailPrice.toFixed(2)}`);
        }
        // Construct Brandclub URL
        const url = `${window.location.origin}/product/p/${product.stacklineSku}/product`;
        parts.push(`URL: ${url}`);
        // Add feature bullets for featured products
        if (isFeatured && product.featureBullets && product.featureBullets.length > 0) {
            parts.push(`\nFeatures:`);
            product.featureBullets.forEach((bullet) => {
                parts.push(`• ${bullet}`);
            });
        }
        return parts.join('\n');
    };
    const convertProductTagsToText = (text) => {
        let result = text;
        // Replace featured product tags (with or without self-closing slash)
        // Handle multiple featured products by looking them up in productMetadataMap
        result = result.replace(/<product\s+featured="true"\s+stacklineSku="([^"]+)"\s*(?:question="[^"]+")?\s*\/?>/g, (match, stacklineSku) => {
            // First try to find in productMetadataMap (for multiple featured products)
            let product = productMetadataMap.get(stacklineSku);
            // Fall back to props for backward compatibility (single featured product)
            if (!product) {
                product = (featuredProduct === null || featuredProduct === void 0 ? void 0 : featuredProduct.product) || (streamingFeaturedProduct === null || streamingFeaturedProduct === void 0 ? void 0 : streamingFeaturedProduct.product);
                if (product && product.stacklineSku !== stacklineSku) {
                    product = undefined; // Clear if SKU doesn't match
                }
            }
            if (product) {
                return '\n\n' + formatProductForCopy(product, true) + '\n\n';
            }
            return match;
        });
        // Replace product group tags
        result = result.replace(/<product_group[^>]*>([\s\S]*?)<\/product_group>/g, (match, innerContent) => {
            // Extract product tags from within the group (with or without self-closing slash)
            const productTags = innerContent.match(/<product\s+stacklineSku="([^"]+)"\s*\/?>/g);
            if (!productTags)
                return match;
            const products = [];
            productTags.forEach((tag) => {
                const skuMatch = tag.match(/stacklineSku="([^"]+)"/);
                if (skuMatch) {
                    const sku = skuMatch[1];
                    const product = productMetadataMap.get(sku);
                    if (product) {
                        products.push(formatProductForCopy(product, false));
                    }
                }
            });
            return products.length > 0 ? '\n\n' + products.join('\n---\n') + '\n\n' : match;
        });
        // Replace citation tags with source URL
        result = result.replace(/<citation\s+[^>]*>([\s\S]*?)<\/citation>/g, (match, citationText) => {
            // Extract source attribute
            const sourceMatch = match.match(/source\s*=\s*"([^"]+)"/);
            const source = sourceMatch ? sourceMatch[1] : '';
            if (source) {
                return `[${citationText.trim()}](${source})`;
            }
            return citationText;
        });
        return result;
    };
    const handleCopy = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const textWithProducts = convertProductTagsToText(content);
            yield navigator.clipboard.writeText(textWithProducts);
            toast.success('Message copied to clipboard');
        }
        catch (error) {
            toast.error('Failed to copy message');
        }
    });
    const handleShare = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!conversationId) {
            console.warn('Cannot share: missing conversationId');
            return;
        }
        try {
            // Get headers with auth and visitor ID
            const headers = yield getHeadersWithUniqueVisitorId();
            // Enable sharing for the conversation
            const result = yield setConversationSharing({
                conversationId,
                enabled: true,
                headersToInject: headers,
                apiBaseUrl,
            });
            // Copy the share URL to clipboard
            yield navigator.clipboard.writeText(result.shareUrl);
            toast.success('Share link copied to clipboard');
        }
        catch (error) {
            console.error('Failed to share conversation:', error);
            toast.error('Failed to share conversation');
        }
    });
    const handleLike = () => __awaiter(void 0, void 0, void 0, function* () {
        var _a;
        if (!conversationId || messageIndex === undefined) {
            toast.error('Cannot like: conversation not loaded');
            return;
        }
        try {
            const newState = !liked;
            // Get headers with auth and visitor ID
            const headers = yield getHeadersWithUniqueVisitorId();
            // Call API to persist feedback FIRST
            const result = yield sendMessageFeedback({
                conversationId,
                messageIndex,
                feedbackType: newState ? 'like' : null,
                headersToInject: headers,
                apiBaseUrl,
            });
            // Only update UI state after successful API call
            setLiked(newState);
            setDisliked(false);
            toast.success(newState ? 'Message liked' : 'Like removed');
            // Update parent component state with new feedback
            if (onUpdateFeedback && ((_a = result.message.metadata) === null || _a === void 0 ? void 0 : _a.feedback)) {
                onUpdateFeedback(messageIndex, result.message.metadata.feedback);
            }
        }
        catch (error) {
            console.error('Failed to update feedback:', error);
            toast.error('Failed to save feedback');
        }
    });
    const handleDislike = () => __awaiter(void 0, void 0, void 0, function* () {
        var _b;
        if (!conversationId || messageIndex === undefined) {
            toast.error('Cannot dislike: conversation not loaded');
            return;
        }
        try {
            const newState = !disliked;
            // Get headers with auth and visitor ID
            const headers = yield getHeadersWithUniqueVisitorId();
            // Call API to persist feedback FIRST
            const result = yield sendMessageFeedback({
                conversationId,
                messageIndex,
                feedbackType: newState ? 'dislike' : null,
                headersToInject: headers,
                apiBaseUrl,
            });
            // Only update UI state after successful API call
            setDisliked(newState);
            setLiked(false);
            toast.success(newState ? 'Message disliked' : 'Dislike removed');
            // Update parent component state with new feedback
            if (onUpdateFeedback && ((_b = result.message.metadata) === null || _b === void 0 ? void 0 : _b.feedback)) {
                onUpdateFeedback(messageIndex, result.message.metadata.feedback);
            }
        }
        catch (error) {
            console.error('Failed to update feedback:', error);
            toast.error('Failed to save feedback');
        }
    });
    // Helper to determine if message is short (for pill shape)
    const isShortMessage = (text, threshold) => {
        return !text.includes('\n') && text.length < threshold;
    };
    // Render user message bubble with widget styling
    const renderUserMessageBubble = () => (React__default.createElement(MessageBubbleWidget, { widgetId: "concierge-user-message", initialState: {
            bgColor: brandclub_colors.blueWhite,
            textColor: theme.palette.text.primary,
            maxWidth: '70%',
        } }, (styles) => {
        const isShort = isShortMessage(content, styles.shortMessageThreshold);
        const borderRadius = isShort ? styles.borderRadiusShort : styles.borderRadius;
        return (React__default.createElement("div", { style: { maxWidth: styles.maxWidth } },
            React__default.createElement("div", { className: "message-content user-message", style: {
                    maxWidth: '100%',
                    minWidth: '50px',
                    backgroundColor: styles.bgColor,
                    color: styles.textColor,
                    borderRadius: `${borderRadius}px`,
                    wordWrap: 'break-word',
                    padding: `${styles.paddingY}px ${styles.paddingX}px`,
                    fontSize: `${styles.fontSize}px`,
                    fontWeight: styles.fontWeight,
                    lineHeight: styles.lineHeight,
                } },
                React__default.createElement("div", { style: { whiteSpace: 'pre-wrap' } }, content))));
    }));
    // Render assistant message bubble with widget styling
    const renderAssistantMessageBubble = () => (React__default.createElement(MessageBubbleWidget, { widgetId: "concierge-assistant-message", initialState: {
            bgColor: '#ffffff',
            textColor: theme.palette.text.primary,
            maxWidth: '100%',
            paddingX: 0,
            paddingY: 0,
        } }, (styles) => (React__default.createElement("div", { style: { maxWidth: styles.maxWidth } },
        React__default.createElement("div", { className: "message-content", style: {
                maxWidth: '100%',
                minWidth: '50px',
                backgroundColor: styles.bgColor,
                color: styles.textColor,
                borderRadius: `${styles.borderRadius}px`,
                wordWrap: 'break-word',
                padding: `${styles.paddingY}px ${styles.paddingX}px`,
                fontSize: `${styles.fontSize}px`,
                fontWeight: styles.fontWeight,
                lineHeight: styles.lineHeight,
            } },
            renderMessageContentWithCarousels(content, productMetadataMap, streamingProductGroups, isStreaming, isStreaming),
            isStreaming && content.length > 0 && (React__default.createElement("span", { style: {
                    display: 'inline-block',
                    width: '2px',
                    height: '1em',
                    backgroundColor: 'currentColor',
                    animation: 'blink 1s infinite',
                    marginLeft: '2px',
                } }))),
        bottomRef && (React__default.createElement("div", { style: {
                height: '1px',
                width: '100%',
            }, ref: bottomRef })),
        !isTyping && !isStreaming && content.length > 0 ? (React__default.createElement(Actions, { className: "tw:mt-2" },
            React__default.createElement(Action, { label: "Copy", tooltip: "Copy", onClick: handleCopy },
                React__default.createElement(CopyIcon, { className: "tw:size-4" })),
            React__default.createElement(Action, { label: "Like", tooltip: "Like", onClick: handleLike },
                React__default.createElement(ThumbsUpIcon, { className: `tw:size-4 ${liked ? 'tw:fill-current tw:text-primary' : ''}` })),
            React__default.createElement(Action, { label: "Dislike", tooltip: "Dislike", onClick: handleDislike },
                React__default.createElement(ThumbsDownIcon, { className: `tw:size-4 ${disliked ? 'tw:fill-current tw:text-primary' : ''}` })),
            React__default.createElement(Action, { label: "Share", tooltip: "Share", onClick: handleShare },
                React__default.createElement(ShareIcon, { className: "tw:size-4" })))) : null))));
    return (React__default.createElement("div", { className: `message ${role}`, style: {
            display: 'flex',
            justifyContent: isUser ? 'flex-end' : 'flex-start',
            flexDirection: 'column',
            alignItems: isUser ? 'flex-end' : 'flex-start',
        } },
        !isUser && (previewSearchQueries.length > 0 || previewQueryProducts.length > 0) && (React__default.createElement(ChainOfThought, { previewSearchQueries: previewSearchQueries, previewQueryProducts: previewQueryProducts, isStreaming: isStreaming, currentStatus: currentStatus, hasContent: content.length > 0, isLastMessage: isLastMessage })),
        !isUser && isTyping && (React__default.createElement("div", { style: { maxWidth: '100%' } },
            React__default.createElement("div", { className: "message-content", style: {
                    maxWidth: '100%',
                    backgroundColor: '#fff',
                    color: theme.palette.text.primary,
                    borderRadius: '18px',
                    borderBottomLeftRadius: '5px',
                    borderBottomRightRadius: '18px',
                    wordWrap: 'break-word',
                    padding: '0.75rem 1rem',
                } },
                React__default.createElement(Shimmer, { as: "p", className: "tw:text-sm", duration: 1.5 }, "Thinking...")))),
        isUser && (imageUrl || imageData) && (React__default.createElement("div", { style: {
                maxWidth: '70%',
                marginBottom: '0.5rem',
            } },
            React__default.createElement("img", { src: imageUrl || `data:${imageMimeType};base64,${imageData}`, alt: "User uploaded image", style: {
                    width: '100%',
                    maxHeight: '400px',
                    objectFit: 'cover',
                    display: 'block',
                    borderRadius: '12px',
                } }))),
        !isTyping && (isUser ? renderUserMessageBubble() : renderAssistantMessageBubble())));
});
// Memoize the component to prevent unnecessary re-renders
const ChatMessage = React__default.memo(ChatMessageComponent, (prevProps, nextProps) => {
    var _a, _b, _c, _d, _e, _f;
    // Custom comparison function to prevent re-renders when props haven't meaningfully changed
    return (prevProps.role === nextProps.role &&
        prevProps.content === nextProps.content &&
        prevProps.isTyping === nextProps.isTyping &&
        prevProps.bottomRef === nextProps.bottomRef &&
        prevProps.isStreaming === nextProps.isStreaming &&
        prevProps.isLastMessage === nextProps.isLastMessage &&
        prevProps.currentStatus === nextProps.currentStatus &&
        prevProps.isWaitingForFollowUpQueries === nextProps.isWaitingForFollowUpQueries &&
        ((_a = prevProps.previewSearchQueries) === null || _a === void 0 ? void 0 : _a.length) === ((_b = nextProps.previewSearchQueries) === null || _b === void 0 ? void 0 : _b.length) &&
        ((_c = prevProps.previewQueryProducts) === null || _c === void 0 ? void 0 : _c.length) === ((_d = nextProps.previewQueryProducts) === null || _d === void 0 ? void 0 : _d.length) &&
        ((_e = prevProps.followUpQueries) === null || _e === void 0 ? void 0 : _e.length) === ((_f = nextProps.followUpQueries) === null || _f === void 0 ? void 0 : _f.length) &&
        prevProps.productMetadataMap === nextProps.productMetadataMap &&
        prevProps.streamingProductGroups === nextProps.streamingProductGroups &&
        prevProps.featuredProduct === nextProps.featuredProduct &&
        prevProps.streamingFeaturedProduct === nextProps.streamingFeaturedProduct &&
        prevProps.imageUrl === nextProps.imageUrl &&
        prevProps.imageData === nextProps.imageData &&
        prevProps.imageMimeType === nextProps.imageMimeType);
});

export { ChatMessage };
//# sourceMappingURL=ChatMessage.js.map
